import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, rows, type ID, type Paginated, type User } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { AutoGrid } from '../../payroll/components/common';
import { api } from '../../../lib/api';
import { fieldErrors } from '../../../lib/errors';
import { useDebounced } from '../../../lib/hooks';
import { useMut } from '../../../lib/query';
import { Avatar, Badge, Button, Card, Checkbox, DataTable, Modal, PageHeader, Row, Screen, Segmented, Stack, Switch, Text, TextField, useTheme } from '../../../ui';
import { EmployeePicker } from '../../payroll/components/EmployeePicker';

type Form = Partial<User> & { password?: string; role_ids?: ID[] };
type ActiveFilter = 'all' | '1' | '0';

export function UsersScreen() {
  const { colors } = useTheme();
  const { me } = useAuth();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [active, setActive] = useState<ActiveFilter>('all');
  const ds = useDebounced(search);
  const q = useQuery({ queryKey: ['users', ds, page, active], queryFn: () => api.admin.users.list({ search: ds || undefined, page, is_active: active === 'all' ? undefined : active }) });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.admin.roles.list() });
  const [f, setF] = useState<Form | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [pw, setPw] = useState<{ user: User; password: string } | null>(null);
  const [pwErr, setPwErr] = useState('');

  const save = useMut(
    () => {
      const body: Form = { name: f!.name, email: f!.email, sso_id: f!.sso_id || null, employee_id: f!.employee_id ?? null, is_active: !!f!.is_active, role_ids: f!.role_ids ?? [] };
      if (f!.password) body.password = f!.password;
      return f!.id ? api.admin.users.update(f!.id, body) : api.admin.users.create(body);
    },
    { invalidate: [['users'], ['roles']], success: 'User saved', onSuccess: () => setF(null), onError: (e) => setErrs(fieldErrors(e)) },
  );
  const toggleActive = useMut((u: User) => api.admin.users.update(u.id, { is_active: !u.is_active }), { invalidate: [['users']], success: (u) => (u.is_active ? 'User activated' : 'User deactivated') });
  const resetPw = useMut(() => api.admin.users.update(pw!.user.id, { password: pw!.password }), {
    success: 'Password reset',
    onSuccess: () => setPw(null),
    onError: (e) => setPwErr(fieldErrors(e).password ?? ''),
    silentError: false,
  });
  const open = (u: User | null) => {
    setErrs({});
    setF(u ? { ...u, role_ids: u.roles?.map((r) => r.id) ?? [] } : { name: '', email: '', is_active: true, role_ids: [] });
  };
  const data: Paginated<User> | User[] | undefined = q.data && !Array.isArray(q.data) ? q.data : rows(q.data);

  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="users">
      <PageHeader
        title="Users & SSO"
        subtitle="Login accounts. SSO users sign in through your identity provider; the SSO subject ID links the account."
        actions={<Button variant="primary" icon="plus" title="Invite user" onPress={() => open(null)} testID="new-user" />}
      />
      <Card>
        <Row gap={12}>
          <TextField
            placeholder="Search name or email…"
            value={search}
            onChangeText={(v) => {
              setSearch(v);
              setPage(1);
            }}
            accessibilityLabel="Search users"
            containerStyle={{ flex: 1, minWidth: 200 }}
          />
          <Segmented<ActiveFilter>
            value={active}
            onChange={(v) => {
              setActive(v);
              setPage(1);
            }}
            options={[
              { key: 'all', label: 'All' },
              { key: '1', label: 'Active' },
              { key: '0', label: 'Disabled' },
            ]}
          />
        </Row>
      </Card>
      <DataTable
        rows={data}
        loading={q.isLoading}
        onPageChange={setPage}
        onRowPress={open}
        emptyTitle="No users"
        testID="users-table"
        columns={[
          {
            key: 'name',
            title: 'User',
            width: 220,
            flex: 2,
            sortValue: (u) => u.name,
            render: (u) => (
              <Row gap={8} wrap={false}>
                <Avatar name={u.name} size={28} />
                <View style={{ flexShrink: 1 }}>
                  <Text weight="medium" numberOfLines={1}>
                    {u.name}
                  </Text>
                  <Text variant="small" numberOfLines={1}>
                    {u.email}
                  </Text>
                </View>
              </Row>
            ),
          },
          {
            key: 'roles',
            title: 'Roles',
            width: 180,
            flex: 2,
            sortable: false,
            render: (u) => (
              <Row gap={4}>
                {(u.roles ?? []).map((r) => (
                  <Badge key={r.id} tone="primary">
                    {r.name}
                  </Badge>
                ))}
                {!u.roles?.length && <Text variant="small">No roles</Text>}
              </Row>
            ),
          },
          { key: 'sso_id', title: 'Sign-in', width: 130, render: (u) => (u.sso_id ? <Badge tone="info">SSO</Badge> : <Text variant="small">Password</Text>), sortValue: (u) => (u.sso_id ? 1 : 0) },
          { key: 'last_login_at', title: 'Last login', width: 110, render: (u) => fmtDate(u.last_login_at), sortValue: (u) => u.last_login_at },
          { key: 'is_active', title: 'Status', width: 100, render: (u) => <Badge tone={u.is_active ? 'success' : 'neutral'}>{u.is_active ? 'Active' : 'Disabled'}</Badge>, sortValue: (u) => (u.is_active ? 1 : 0) },
          {
            key: 'actions',
            title: '',
            width: 220,
            sortable: false,
            render: (u) => (
              <Row gap={6}>
                <Button size="sm" title={u.is_active ? 'Deactivate' : 'Activate'} variant={u.is_active ? 'ghost' : 'success'} disabled={u.id === me?.id} loading={toggleActive.isPending && toggleActive.variables?.id === u.id} onPress={() => toggleActive.mutate(u)} />
                <Button
                  size="sm"
                  icon="key"
                  title="Reset password"
                  onPress={() => {
                    setPwErr('');
                    setPw({ user: u, password: '' });
                  }}
                />
              </Row>
            ),
          },
        ]}
      />
      <Modal
        open={!!f}
        onClose={() => setF(null)}
        title={f?.id ? 'Edit user' : 'Invite user'}
        width={640}
        testID="user-form"
        footer={
          <>
            <Button title="Cancel" onPress={() => setF(null)} />
            <Button variant="primary" title="Save" loading={save.isPending} onPress={() => save.mutate(undefined)} testID="save-user" />
          </>
        }
      >
        {f && (
          <Stack gap={14}>
            <AutoGrid min={220}>
              <TextField label="Name" required value={f.name ?? ''} error={errs.name} onChangeText={(v) => setF({ ...f, name: v })} testID="user-name" />
              <TextField label="Email" required keyboardType="email-address" autoCapitalize="none" value={f.email ?? ''} error={errs.email} onChangeText={(v) => setF({ ...f, email: v })} testID="user-email" />
              <TextField
                label={f.id ? 'New password' : 'Password'}
                secureTextEntry
                autoComplete="new-password"
                value={f.password ?? ''}
                error={errs.password}
                hint={f.id ? 'Leave blank to keep the current password' : 'Leave blank to send an invite link'}
                onChangeText={(v) => setF({ ...f, password: v || undefined })}
              />
              <TextField label="SSO subject ID" autoCapitalize="none" value={f.sso_id ?? ''} error={errs.sso_id} hint="From Google Workspace / Azure AD / Okta" onChangeText={(v) => setF({ ...f, sso_id: v || null })} />
            </AutoGrid>
            <EmployeePicker label="Linked employee" clearable value={f.employee_id} error={errs.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} hint="Links ESS data (payslips, leave) to this login" />
            <View style={{ gap: 10 }}>
              <Text variant="label">Roles</Text>
              {rows(roles.data).map((r) => (
                <Checkbox
                  key={r.id}
                  label={`${r.name}${r.is_system ? ' (system)' : ''}`}
                  value={!!f.role_ids?.includes(r.id)}
                  onChange={(v) => setF({ ...f, role_ids: v ? [...(f.role_ids ?? []), r.id] : (f.role_ids ?? []).filter((x) => x !== r.id) })}
                />
              ))}
              {!!errs.role_ids && (
                <Text size={12} color={colors.danger}>
                  {errs.role_ids}
                </Text>
              )}
            </View>
            <Switch label="Active" description="Disabled users can't sign in and their sessions are revoked" value={!!f.is_active} disabled={f.id === me?.id} onChange={(v) => setF({ ...f, is_active: v })} />
          </Stack>
        )}
      </Modal>
      <Modal
        open={!!pw}
        onClose={() => setPw(null)}
        title="Reset password"
        subtitle={pw?.user.email}
        width={420}
        footer={
          <>
            <Button title="Cancel" onPress={() => setPw(null)} />
            <Button variant="primary" title="Reset" disabled={!pw || pw.password.length < 8} loading={resetPw.isPending} onPress={() => resetPw.mutate(undefined)} />
          </>
        }
      >
        {pw && (
          <TextField label="New password" secureTextEntry autoComplete="new-password" value={pw.password} error={pwErr} hint="At least 8 characters. Share it securely with the user." onChangeText={(v) => setPw({ ...pw, password: v })} />
        )}
      </Modal>
    </Screen>
  );
}
